/**
 * Auto-hide flyout hooks: the leave hold and the exit-motion presence.
 * Gallery SoT: /?test=maater-ui#panel-collapse
 */
import {
  useEffect,
  useRef,
  useState,
  type AnimationEvent as ReactAnimationEvent,
} from 'react'
import {
  createShellFlyoutEnterDelay,
  createShellFlyoutHold,
  type ShellFlyoutEnterDelay,
  type ShellFlyoutHold,
} from '@/lib/shell-flyout-hold'
import { SHELL_PANEL_MOTION_MS } from '@/lib/shell-panel-motion'

/** Stable hold controller. `close` may change between renders. */
export function useShellFlyoutHold(close: () => void): ShellFlyoutHold {
  const closeRef = useRef(close)
  closeRef.current = close
  const holdRef = useRef<ShellFlyoutHold | null>(null)
  if (!holdRef.current) holdRef.current = createShellFlyoutHold(() => closeRef.current())
  useEffect(() => () => holdRef.current?.dispose(), [])
  return holdRef.current
}

/** Stable enter delay. `open` may change between renders. */
export function useShellFlyoutEnterDelay(open: () => void): ShellFlyoutEnterDelay {
  const openRef = useRef(open)
  openRef.current = open
  const delayRef = useRef<ShellFlyoutEnterDelay | null>(null)
  if (!delayRef.current) {
    delayRef.current = createShellFlyoutEnterDelay(() => openRef.current())
  }
  useEffect(() => () => delayRef.current?.dispose(), [])
  return delayRef.current
}

/**
 * Keeps the flyout mounted while `exitClass` plays after `show` goes false.
 * With no exit class (instant), it unmounts at once.
 */
export function useShellFlyoutPresence(show: boolean, exitClass: string) {
  const [mounted, setMounted] = useState(false)
  const [closing, setClosing] = useState(false)
  const mountedRef = useRef(false)
  const showRef = useRef(show)
  showRef.current = show

  const unmount = () => {
    mountedRef.current = false
    setMounted(false)
    setClosing(false)
  }

  useEffect(() => {
    if (show) {
      mountedRef.current = true
      setMounted(true)
      setClosing(false)
      return
    }
    if (!mountedRef.current) return
    if (!exitClass) {
      unmount()
      return
    }
    setClosing(true)
    const timer = window.setTimeout(() => {
      if (!showRef.current) unmount()
    }, SHELL_PANEL_MOTION_MS + 40)
    return () => window.clearTimeout(timer)
  }, [show, exitClass])

  const onAnimationEnd = (event: ReactAnimationEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return
    if (showRef.current || !closing) return
    unmount()
  }

  return { mounted, closing, onAnimationEnd }
}
